import { useMemo } from "react";
import { useParams } from "react-router";
import { skipToken } from "@tanstack/react-query";
import {
  useGetFileTreeApiV1ExploreDatasetIdFileTreeGet,
  type GetFileTreeApiV1ExploreDatasetIdFileTreeGetResponse,
} from "vl-api/vlComponents";

export interface FileFolderItem {
  id: string;
  full_path: string;
  name: string;
  selected: boolean;
}

interface FileTreeNode {
  type?: string;
  full_path?: string;
  name?: string;
  children?: FileTreeNode[];
}

function collectFileTreeNodes(root: FileTreeNode): FileTreeNode[] {
  const nodes: FileTreeNode[] = [];
  const walk = (node: FileTreeNode) => {
    nodes.push(node);
    node.children?.forEach(walk);
  };
  walk(root);
  return nodes;
}

function partitionFileTree(
  treeData: GetFileTreeApiV1ExploreDatasetIdFileTreeGetResponse | undefined,
): {
  files: FileFolderItem[];
  folders: FileFolderItem[];
} {
  if (treeData == null || typeof treeData !== "object") {
    return { files: [], folders: [] };
  }

  const nodes = collectFileTreeNodes(treeData as FileTreeNode);
  let fileId = 1;
  let folderId = 1;

  return nodes.reduce(
    (out, node) => {
      const { type, full_path: fullPath, name } = node;
      if (type === "FOLDER") {
        out.folders.push({
          id: String(folderId++),
          full_path: fullPath ?? "",
          name: name ?? "",
          selected: false,
        });
      } else if (!["FOLDER", "ROOT"].includes(type ?? "")) {
        out.files.push({
          id: String(fileId++),
          full_path: fullPath ?? "",
          name: name ?? "",
          selected: false,
        });
      }
      return out;
    },
    { files: [] as FileFolderItem[], folders: [] as FileFolderItem[] },
  );
}

export const useTreeFolderData = () => {
  const { datasetId } = useParams();

  const { data, isLoading } = useGetFileTreeApiV1ExploreDatasetIdFileTreeGet(
    datasetId ? { pathParams: { datasetId } } : skipToken,
    {
      refetchOnMount: false,
    },
  );

  const { files, folders } = useMemo(() => partitionFileTree(data), [data]);

  return {
    isLoading,
    files,
    folders,
  };
};
